import { useState, useCallback, useEffect } from "react";
import type { ReactNode } from "react";
import { ToastContext } from "./Context";
import { ToastContainer } from "./Container";
import type { Toast, ToastType } from "./types";
import { globalToastRef } from "../../query/globalToastRef";

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider = ({ children }: ToastProviderProps) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [exitingToasts, setExitingToasts] = useState<Set<string>>(new Set());

  const removeToast = useCallback((id: string) => {
    setExitingToasts((prev) => new Set(prev).add(id));
    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
      setExitingToasts((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }, 400); // Wait for fade-out animation
  }, []);

  const showToast = useCallback(
    (message: string, type: ToastType = "success") => {
      const id = Math.random().toString(36).substring(7);
      const newToast: Toast = { id, message, type };

      setToasts((prev) => [...prev, newToast]);

      // Loading toasts are removed by the caller; others auto-remove after 5s
      if (type !== "loading") {
        setTimeout(() => {
          setExitingToasts((prev) => new Set(prev).add(id));
          setTimeout(() => {
            setToasts((prev) => prev.filter((toast) => toast.id !== id));
            setExitingToasts((prev) => {
              const newSet = new Set(prev);
              newSet.delete(id);
              return newSet;
            });
          }, 400);
        }, 4600);
      }
      return id;
    },
    [],
  );

  useEffect(() => {
    globalToastRef.current = showToast;
    return () => {
      globalToastRef.current = null;
    };
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, removeToast }}>
      {children}
      <ToastContainer
        toasts={toasts}
        removeToast={removeToast}
        exitingToasts={exitingToasts}
      />
    </ToastContext.Provider>
  );
};
